import './edgeless-group-component';

import type { NoteDisplayMode } from '@blocksuite/blocks';
import {
  BlockModel,
  defineBlockSchema,
  type SchemaToModel,
} from '@blocksuite/store';
import { literal } from 'lit/static-html.js';

import { EdgelessElementGroupsBlockService } from './edgeless-group-service';

/**
 * Represents a single managed note element within the group.
 */
export type ManagedNoteElement = {
  id: string;
  originalDisplayMode: NoteDisplayMode | string;
};

export type ElementDisplayMap = Record<string, boolean>;
/**
 * Represents a single managed surface element (shape, text, etc.) within the group.
 * Stores the necessary properties to recreate it.
 */
export type ManagedSurfaceElement = {
  id: string;
  // Store the element's properties as a plain object for recreation
  properties: Record<string, unknown>;
  elementsArray: ElementDisplayMap;
};

/**
 * Props for the EdgelessElementGroupsBlockModel.
 */
type EdgelessElementManagerProps = {
  managedNoteElements: ManagedNoteElement[];
  managedSurfaceElements: ManagedSurfaceElement[]; // Added for surface elements
  isHidden: boolean; // Added to track visibility state
};

/**
 * Model for the EdgelessElementGroupsBlock.
 * Acts purely as a state container for managed elements (notes and surface elements).
 * The associated service handles initialization and cleanup logic.
 */
export class EdgelessElementGroupsBlockModel extends BlockModel<EdgelessElementManagerProps> {}

/**
 * Schema definition for the EdgelessElementGroupsBlock.
 */
export const EdgelessElementGroupsBlockSchema = defineBlockSchema({
  flavour: 'pique:edgeless-element-tab',
  props: _internal => ({
    // Defines the state properties managed by this block model.
    managedNoteElements: [] as ManagedNoteElement[],
    managedSurfaceElements: [] as ManagedSurfaceElement[], // Added schema prop
    isHidden: true, // Initialize as hidden
  }),
  metadata: {
    version: 1,
    role: 'hub', // Indicates it manages or relates to other blocks
    parent: ['affine:page'], // Can only exist directly under the page
    children: [], // Does not directly contain other blocks in hierarchy
  },
  toModel: () => new EdgelessElementGroupsBlockModel(), // Factory function
});

// Block Spec: Links schema, service, and component
export const EdgelessElementGroupsBlockSpec = {
  schema: EdgelessElementGroupsBlockSchema,
  service: EdgelessElementGroupsBlockService,
  view: {
    // Specifies the non-visual component associated with this block
    component: literal`pique-edgeless-element-tab`,
  },
};

// Type Exports for convenience
export type EdgelessElementGroupsBlockSchema =
  typeof EdgelessElementGroupsBlockSchema;
export type EdgelessElementGroupsBlockModelType = SchemaToModel<
  typeof EdgelessElementGroupsBlockSchema
>;

// Declare block model type globally for BlockSuite integration
/* eslint-disable @typescript-eslint/no-namespace, @typescript-eslint/prefer-namespace-keyword */
declare global {
  namespace BlockSuite {
    interface BlockModels {
      'pique:edgeless-element-tab': EdgelessElementGroupsBlockModel;
    }
    interface EdgelessBlockModelMap {
      'pique:edgeless-element-tab': EdgelessElementGroupsBlockModel;
    }
  }
}
